import React from 'react'
import { isDev } from '@agtech/core/utilities/AgTechUtilities';
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import { AgTechFormSection } from '@agtech/web/components/Forms/Layout/AgTechFormSection';
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { AgTechContact, DefaultAgTechContact } from '@agtech/data/contacts/ContactData';
import { useContactDataWriteContactAction } from '@agtech/data/contacts/ContactDataActions';
import { ContactDataGetAllContactsQuery } from '@agtech/data/contacts/ContactDataQueries';

export type ContactDialogProps = {
    contactId?: number,
    onContactSubmitted?: (submission: AgTechContact) => Promise<void>
}

const ContactDialog = (props: ContactDialogProps) => {    
    const getContactsQuery = useAgTechQuery({
        query: ContactDataGetAllContactsQuery
    });
    
    const writeContactAction = useContactDataWriteContactAction();

    return (
        <AgTechFormDialog
            width={550}
            structure={() => ({
                header: props.contactId ? `Update Contact` : 'Create Contact',
            })}
            dialogData={{
                query: getContactsQuery,
                getDialogData: d => {
                    let dialogContact = props.contactId
                        ? d[props.contactId]
                        : DefaultAgTechContact;

                    // if (isDev() && !props.contactId) {
                    //     const numberOfContacts = Object.values(d).length;
                
                    //     dialogContact = {
                    //         ...DefaultAgTechContact,
                    //         firstName: 'Test',
                    //         lastName: 'Contact' + (numberOfContacts + 1),
                    //         ranchName: 'Test Farms',
                    //         emailAddress: 'TestContact' + (numberOfContacts + 1) + '@gmail.com',
                    //         address: 'Test Address',
                    //         city: 'Fargo',
                    //         stateAbbreviation: 'ND',
                    //         zip: '58104'
                    //     }
                    // }

                    return dialogContact;
                }
            }}
            dialogAction={{
                action: writeContactAction,
                getActionData: d => d
            }}
            dialogBehavior={() => ({
                eventHandlers: {
                    onDialogSubmitted: props.onContactSubmitted ? (data) => {
                        return props.onContactSubmitted!(data);
                    } : undefined
                }
            })}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <AgTechFormRow>
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption="First Name"
                            required={true}
                            focused={true}
                            value={e => e.firstName}
                            onControlValueChanged={async (ent, val) => ({
                                ...ent,
                                firstName: val
                            })}
                        />
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption="Last Name"
                            required={true}
                            value={e => e.lastName}
                            onControlValueChanged={async (ent, val) => ({
                                ...ent,
                                lastName: val
                            })}
                        />
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption="Ranch Name"
                            required={false}
                            value={e => e.ranchName}
                            onControlValueChanged={async (ent, val) => ({
                                ...ent,
                                ranchName: val
                            })}
                        />
                    </AgTechFormRow>
                    <AgTechFormSection header="Contact Information">
                        <AgTechFormRow>
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="Email Address"
                                required={false}
                                value={e => e.emailAddress}
                                onControlValueChanged={async (ent, val) => ({
                                    ...ent,
                                    emailAddress: val
                                })}
                                classes='w-[240px]'
                            />
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="Phone Number"
                                value={e => e.phoneNumber}
                                onControlValueChanged={async (ent, val) => ({
                                    ...ent,
                                    phoneNumber: val
                                })}
                            />
                        </AgTechFormRow>
                    </AgTechFormSection>
                    <AgTechFormSection header="Address Information">
                        <AgTechFormRow>
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="Address"
                                // required={true}
                                value={e => e.address}
                                onControlValueChanged={async (ent, val) => ({ ...ent, address: val })}
                                width={100}
                                classes='wide'
                            />
                        </AgTechFormRow>
                        <AgTechFormRow>
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="City"
                                // required={true}
                                value={e => e.city}
                                onControlValueChanged={async (ent, val) => ({ ...ent, city: val })}
                            />
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="State"
                                // required={true}
                                value={e => e.stateAbbreviation}
                                onControlValueChanged={async (ent, val) => ({ ...ent, stateAbbreviation: val })}
                            />
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption="Zip"
                                // required={true}
                                value={e => e.zip}
                                onControlValueChanged={async (ent, val) => ({ ...ent, zip: val })}
                            />
                        </AgTechFormRow>
                    </AgTechFormSection>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
};

ContactDialog.Open = (e: AgTechClickEventHandler, props: ContactDialogProps) => e.dialogs.openDialog({
    dialog: () => <ContactDialog {...props} />
});

ContactDialog.Button = (props: ContactDialogProps & AgTechButtonProps) => {
    let buttonProps: AgTechButtonProps = {
        text: props.contactId ? 'Update Contact' : 'Create Contact',
        icon: props.contactId ? 'fas fa-edit' : 'fas fa-plus',
        ...props,
        action: e => ContactDialog.Open(e, props)
    }

    return <AgTechButton {...buttonProps} />
}

export default ContactDialog;